import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { RouterLink } from '@angular/router';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { Role } from '../../../models/Role';
import { PermissionsService } from '../../../services/permissions.service';
import { Logger } from '../../../../../infrastructure/logging/logger';
import { DeleteRoleDialogComponent } from './components/delete-role-dialog/delete-role-dialog.component';
import { CreateRoleDialogComponent } from './components/create-role-dialog/create-role-dialog.component';

@Component({
  selector: 'app-roles',
  templateUrl: './roles.component.html',
  styleUrls: ['./roles.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatIcon,
    MatProgressSpinner,
    RouterLink
  ]
})
export class RolesComponent implements OnInit {

  @Output() rolesChanged = new EventEmitter<void>();

  loading = new BehaviorSubject<boolean>(false);
  roles: Role[] = [];
  roleForms: FormGroup[] = [];
  savingRoleId: string | null = null;
  deletingRoleId: string | null = null;
  editingIndex: number | null = null;

  constructor(
    private permissionsService: PermissionsService,
    private formBuilder: FormBuilder,
    private logService: Logger,
    private dialog: MatDialog
  ) { }

  ngOnInit() {
    this.loadPage();
  }

  async loadPage() {
    try {
      this.loading.next(true);
      this.roles = await this.permissionsService.getRoles();
      this.roleForms = this.roles.map(role => this.buildRoleForm(role.name));
    } catch (e) {
      this.logService.handleError(e, 'An error occurred while retrieving Roles', 'Error while retrieving Roles');
    } finally {
      this.loading.next(false);
    }
  }

  private buildRoleForm(name: string = ''): FormGroup {
    return this.formBuilder.group({
      name: [name, [Validators.required, Validators.pattern(/[\S]/)]]
    });
  }

  async onCreateClick(): Promise<void> {
    const role = await firstValueFrom(this.dialog.open(CreateRoleDialogComponent).afterClosed());

    if (!role) {
      return;
    }

    this.roles.push(role);
    this.roleForms.push(this.buildRoleForm(role.name));
    this.rolesChanged.emit();
  }

  onEditClick(index: number): void {
    const form = this.roleForms[index];
    const role = this.roles[index];

    if (!form || !role) {
      return;
    }

    form.patchValue({ name: role.name });
    this.editingIndex = index;
  }

  onCancelClick(index: number): void {
    const form = this.roleForms[index];
    const role = this.roles[index];

    if (!form || !role) {
      return;
    }

    form.patchValue({ name: role.name });
    this.editingIndex = null;
  }

  async onSubmitClick(index: number): Promise<void> {
    const form = this.roleForms[index];
    const role = this.roles[index];

    if (!form || form.invalid) {
      return;
    }

    try {
      this.savingRoleId = role.id;
      const name = (form.value.name as string).trim();
      const updatedRole = await this.permissionsService.updateRole(role.id, name);

      this.roles[index] = updatedRole;
      form.patchValue({ name: updatedRole.name });
      this.editingIndex = null;
      this.rolesChanged.emit();
    } catch (e) {
      this.logService.handleError(e, 'An error occurred while updating the Role', 'Error while updating Role');
    } finally {
      this.savingRoleId = null;
    }
  }

  async onDeleteClick(index: number): Promise<void> {
    const role = this.roles[index];

    if (!role) {
      return;
    }

    const confirmed = await firstValueFrom(this.dialog.open(DeleteRoleDialogComponent, { data: { role } }).afterClosed());

    if (!confirmed) {
      return;
    }

    try {
      this.deletingRoleId = role.id;
      await this.permissionsService.deleteRole(role.id);

      this.roles.splice(index, 1);
      this.roleForms.splice(index, 1);
      this.editingIndex = null;
      this.rolesChanged.emit();
    } catch (e) {
      this.logService.handleError(e, 'An error occurred while deleting the Role', 'Error while deleting Role');
    } finally {
      this.deletingRoleId = null;
    }
  }
}
